/**
 * Hook for fetching previous period data for trend calculations
 * Centralizes previous period fetching logic and provides caching
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { FILTER_PERIODS } from '../constants';
import {
  getUserAnalytics,
  getSplitAnalytics,
  getTransactionAnalytics,
  getSharedWalletAnalytics,
  getBadgeAnalytics,
  getPhoneAuthAnalytics,
  getWaitlistAnalytics,
  getSessionAnalytics
} from '../services/firebaseService';

// Cache for previous period data
const previousPeriodCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

// Pending requests map to prevent duplicate fetches
const pendingRequests = new Map();

/**
 * Get cache key for previous period data
 */
const getCacheKey = (types, period, platform) => {
  const typesKey = Array.isArray(types) ? types.sort().join(',') : types;
  return `prev_${typesKey}_${period}_${platform || 'all'}`;
};

/**
 * Get previous period mapping
 */
const getPreviousPeriod = (currentPeriod) => {
  const periodMap = {
    [FILTER_PERIODS.DAY]: FILTER_PERIODS.DAY,
    [FILTER_PERIODS.WEEK]: FILTER_PERIODS.WEEK,
    [FILTER_PERIODS.MONTH]: FILTER_PERIODS.MONTH,
    [FILTER_PERIODS.YEAR]: FILTER_PERIODS.YEAR,
    [FILTER_PERIODS.ALL]: FILTER_PERIODS.YEAR
  };
  return periodMap[currentPeriod] || FILTER_PERIODS.DAY;
};

/**
 * Analytics type to fetch function mapping
 */
const fetchFunctions = {
  users: getUserAnalytics,
  splits: getSplitAnalytics,
  transactions: getTransactionAnalytics,
  wallets: getSharedWalletAnalytics,
  badges: getBadgeAnalytics,
  phoneAuth: getPhoneAuthAnalytics,
  waitlist: getWaitlistAnalytics,
  sessions: getSessionAnalytics
};

/**
 * Hook to fetch previous period data for multiple analytics types
 * @param {string|string[]} types - Analytics types to fetch ('users', 'splits', etc.)
 * @param {string} period - Current period
 * @param {string|null} platform - Platform filter (optional)
 * @returns {Object} - { data, loading, error }
 */
export const usePreviousPeriodData = (types, period, platform = null) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);

  // Normalize types to array
  const typesArray = Array.isArray(types) ? types : [types];

  const fetchData = useCallback(async () => {
    if (!typesArray.length) {
      setLoading(false);
      return;
    }

    const prevPeriod = getPreviousPeriod(period);
    const cacheKey = getCacheKey(typesArray, prevPeriod, platform);

    // Check cache
    const cached = previousPeriodCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      if (mountedRef.current) {
        setData(cached.data);
        setLoading(false);
        setError(null);
      }
      return;
    }

    // Check pending requests
    if (pendingRequests.has(cacheKey)) {
      try {
        const pendingData = await pendingRequests.get(cacheKey);
        if (mountedRef.current) {
          setData(pendingData);
          setLoading(false);
          setError(null);
        }
        return;
      } catch (err) {
        // If pending request fails, continue with new request
      }
    }

    // Create new request
    const requestPromise = (async () => {
      setLoading(true);
      setError(null);

      try {
        const fetchPromises = typesArray.map(type => {
          const fetchFn = fetchFunctions[type];
          if (!fetchFn) {
            console.warn(`Unknown analytics type: ${type}`);
            return Promise.resolve(null);
          }
          return fetchFn(prevPeriod, platform).catch(err => {
            console.error(`Error fetching previous ${type} data:`, err);
            return null; // Return null on error, don't fail entire request
          });
        });

        const results = await Promise.all(fetchPromises);
        
        // Build data object
        const resultData = {};
        typesArray.forEach((type, index) => {
          resultData[type] = results[index];
        });

        // Cache result
        previousPeriodCache.set(cacheKey, {
          data: resultData,
          timestamp: Date.now()
        });

        if (mountedRef.current) {
          setData(resultData);
          setLoading(false);
          setError(null);
        }

        return resultData;
      } catch (err) {
        if (mountedRef.current) {
          setError(err.message || 'Failed to fetch previous period data');
          setLoading(false);
        }
        throw err;
      } finally {
        pendingRequests.delete(cacheKey);
      }
    })();

    pendingRequests.set(cacheKey, requestPromise);
  }, [typesArray.join(','), period, platform]);

  useEffect(() => {
    mountedRef.current = true;
    fetchData();

    return () => {
      mountedRef.current = false;
    };
  }, [fetchData]);

  // Clear cache function (optional, for manual invalidation)
  const clearCache = useCallback(() => {
    previousPeriodCache.clear();
    pendingRequests.clear();
  }, []);

  return { data, loading, error, clearCache };
};

/**
 * Hook to fetch single previous period data type
 * Convenience wrapper around usePreviousPeriodData
 */
export const usePreviousPeriod = (type, period, platform = null) => {
  const { data, loading, error, clearCache } = usePreviousPeriodData(type, period, platform);
  return {
    data: data[type] || null,
    loading,
    error,
    clearCache
  };
};

